import React, { useState, useRef } from 'react';
import { FiUpload, FiDownload, FiCheckCircle, FiAlertTriangle, FiFile } from 'react-icons/fi';
import { toast } from 'react-toastify';
import productService from '../../services/product.service';

const FarmerBulkImportPage = () => {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [results, setResults] = useState(null);
  const fileRef = useRef(null);

  const handleFileChange = (e) => {
    const f = e.target.files[0];
    if (f) {
      if (f.size > 5 * 1024 * 1024) {
        toast.error('File must be under 5MB');
        return;
      }
      setFile(f);
      setResults(null);
    }
  };

  const handleUpload = async () => {
    if (!file) return;
    setUploading(true);
    try {
      const { data } = await productService.bulkImport(file);
      setResults(data.data);
      toast.success(data.message);
      setFile(null);
      if (fileRef.current) fileRef.current.value = '';
    } catch (error) {
      toast.error(error.response?.data?.message || 'Import failed');
    } finally {
      setUploading(false);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await productService.downloadTemplate();
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'farmdirectly-product-template.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch {
      toast.error('Failed to download template');
    }
  };

  return (
    <div>
      <h2 className="mb-4">Bulk Product Import</h2>

      <div className="row g-4">
        {/* Upload Section */}
        <div className="col-lg-7">
          <div className="dashboard-card">
            <h5 className="mb-3">Upload CSV File</h5>
            <p className="text-muted small mb-3">
              Upload a CSV file with your products. Required columns: <strong>name</strong>, <strong>price</strong>, <strong>category</strong>.
              Optional: description, subcategory, sku, stock, tags (semicolon-separated), compareAtPrice, freeShipping, weight.
            </p>

            <div
              className="border rounded p-4 text-center mb-3"
              style={{
                borderStyle: 'dashed',
                background: file ? '#F0FDF4' : '#F9FAFB',
                cursor: 'pointer',
              }}
              onClick={() => fileRef.current?.click()}
            >
              {file ? (
                <div>
                  <FiFile size={32} className="text-success mb-2" />
                  <p className="mb-1 fw-semibold">{file.name}</p>
                  <small className="text-muted">{(file.size / 1024).toFixed(1)} KB</small>
                </div>
              ) : (
                <div>
                  <FiUpload size={32} className="text-muted mb-2" />
                  <p className="mb-1">Click to select a CSV file</p>
                  <small className="text-muted">Max 5MB</small>
                </div>
              )}
              <input
                ref={fileRef}
                type="file"
                accept=".csv,.xlsx,.xls"
                onChange={handleFileChange}
                className="d-none"
              />
            </div>

            <div className="d-flex gap-2">
              <button
                className="btn btn-fd-primary flex-grow-1"
                onClick={handleUpload}
                disabled={!file || uploading}
              >
                {uploading ? 'Importing...' : 'Import Products'}
              </button>
              <button
                className="btn btn-fd-outline"
                onClick={handleDownloadTemplate}
              >
                <FiDownload className="me-1" /> Template
              </button>
            </div>
          </div>
        </div>

        {/* Instructions */}
        <div className="col-lg-5">
          <div className="dashboard-card h-100">
            <h5 className="mb-3">CSV Format Guide</h5>
            <div className="small">
              <p className="mb-2"><strong>Required columns:</strong></p>
              <ul className="mb-3">
                <li><code>name</code> — Product name</li>
                <li><code>price</code> — Price in USD</li>
                <li><code>category</code> — Product category</li>
              </ul>
              <p className="mb-2"><strong>Optional columns:</strong></p>
              <ul className="mb-3">
                <li><code>description</code> — Full description</li>
                <li><code>sku</code> — Unique SKU code</li>
                <li><code>stock</code> — Inventory count</li>
                <li><code>tags</code> — Semicolon-separated</li>
                <li><code>compareAtPrice</code> — Original price</li>
                <li><code>freeShipping</code> — true/false</li>
                <li><code>weight</code> — Weight in grams</li>
              </ul>
              <p className="text-muted">All imported products start as <strong>Draft</strong>. Activate them from the Products page.</p>
            </div>
          </div>
        </div>
      </div>

      {/* Results */}
      {results && (
        <div className="dashboard-card mt-4">
          <h5 className="mb-3">Import Results</h5>
          <div className="row g-3 mb-3">
            <div className="col-sm-4">
              <div className="p-3 rounded text-center" style={{ background: '#F0FDF4' }}>
                <FiCheckCircle size={24} className="text-success mb-1" />
                <div className="fw-bold fs-4 text-success">{results.created}</div>
                <small className="text-muted">Created</small>
              </div>
            </div>
            <div className="col-sm-4">
              <div className="p-3 rounded text-center" style={{ background: '#FEF3C7' }}>
                <FiAlertTriangle size={24} className="text-warning mb-1" />
                <div className="fw-bold fs-4 text-warning">{results.skipped}</div>
                <small className="text-muted">Skipped</small>
              </div>
            </div>
            <div className="col-sm-4">
              <div className="p-3 rounded text-center bg-light">
                <div className="fw-bold fs-4">{results.created + results.skipped}</div>
                <small className="text-muted">Total Rows</small>
              </div>
            </div>
          </div>

          {results.errors?.length > 0 && (
            <div>
              <h6 className="text-danger mb-2">Errors:</h6>
              <div className="table-responsive" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                <table className="table table-sm mb-0">
                  <thead><tr><th>Row</th><th>Error</th></tr></thead>
                  <tbody>
                    {results.errors.map((err, i) => (
                      <tr key={i}>
                        <td className="text-muted">{err.row}</td>
                        <td className="small">{err.message}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default FarmerBulkImportPage;
